import { create } from 'zustand'
import { llmModelsService } from '@/services/llm-models'
import type {
  LLMProviderValue,
  ModelCatalogEntry,
} from '@/schemas/llm-models'

/**
 * Phase 4 plan 04-04: per-session catalog cache (RESEARCH §7, D-04).
 *
 * No react-query / SWR in this repo — plain Zustand with a 5-minute staleness
 * window matches existing store conventions. `loadCatalog` is a no-op while the
 * cached data is fresh, so callers can invoke it unconditionally on mount.
 */

const STALE_MS = 5 * 60 * 1000

interface LLMModelsState {
  catalog: Record<LLMProviderValue, ModelCatalogEntry[]>
  loading: Record<LLMProviderValue, boolean>
  error: Record<LLMProviderValue, string | null>
  fetchedAt: Record<LLMProviderValue, number | null>
  loadCatalog: (provider: LLMProviderValue) => Promise<void>
}

export const useLLMModelsStore = create<LLMModelsState>((set, get) => ({
  catalog: { openai: [], anthropic: [], google: [] },
  loading: { openai: false, anthropic: false, google: false },
  error: { openai: null, anthropic: null, google: null },
  fetchedAt: { openai: null, anthropic: null, google: null },

  loadCatalog: async (provider) => {
    const state = get()
    const ts = state.fetchedAt[provider]
    if (ts && Date.now() - ts < STALE_MS) return

    set((s) => ({
      loading: { ...s.loading, [provider]: true },
      error: { ...s.error, [provider]: null },
    }))

    try {
      const resp = await llmModelsService.listModels(provider)
      set((s) => ({
        catalog: { ...s.catalog, [provider]: resp.models },
        loading: { ...s.loading, [provider]: false },
        fetchedAt: { ...s.fetchedAt, [provider]: Date.now() },
      }))
    } catch (e: unknown) {
      set((s) => ({
        loading: { ...s.loading, [provider]: false },
        error: {
          ...s.error,
          [provider]: e instanceof Error ? e.message : 'Failed to load catalog',
        },
      }))
    }
  },
}))
